/**
 * Video Decoder Service
 * Handles video frame decoding using WebCodecs API with fallback to JPEG
 */

import { VideoCodec, FrameData, VideoStats, DecoderConfig } from '../types';

export interface VideoDecoderEvents {
    onFrameDecoded?: (bitmap: ImageBitmap) => void;
    onStatsUpdate?: (stats: VideoStats) => void;
    onError?: (error: Error) => void;
}

/**
 * Check if WebCodecs API is available
 */
export function isWebCodecsSupported(): boolean {
    return typeof VideoDecoder !== 'undefined' && typeof VideoEncoder !== 'undefined';
}

export class VideoDecoderService {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D | null;
    private offscreenCanvas: OffscreenCanvas | null = null;
    private offscreenCtx: OffscreenCanvasRenderingContext2D | null = null;

    private h264Decoder: VideoDecoder | null = null;
    private vp8Decoder: VideoDecoder | null = null;
    private currentCodec: VideoCodec = VideoCodec.Jpeg;
    private isDecoderInitialized = false;
    private h264ReceivedKeyFrame = false;

    private events: VideoDecoderEvents;

    // Statistics
    private frameCount = 0;
    private droppedFrames = 0;
    private totalDecodeTime = 0;
    private totalRenderTime = 0;
    private lastFrameTime = 0;
    private fpsCounter = 0;
    private fpsLastUpdate = 0;
    private currentFps = 0;
    private currentBitrate = 0;
    private bytesReceived = 0;
    private bitrateLastUpdate = 0;

    constructor(canvas: HTMLCanvasElement, events: VideoDecoderEvents = {}) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d', {
            alpha: false,
            desynchronized: true // Lower latency
        });
        this.events = events;

        // Try to use OffscreenCanvas for better performance
        if (typeof OffscreenCanvas !== 'undefined') {
            try {
                this.offscreenCanvas = new OffscreenCanvas(canvas.width, canvas.height);
                this.offscreenCtx = this.offscreenCanvas.getContext('2d', {
                    alpha: false,
                    desynchronized: true
                });
            } catch (e) {
                console.warn('OffscreenCanvas not available:', e);
            }
        }
    }

    /**
     * Initialize decoder for a specific codec
     */
    async initialize(config: DecoderConfig): Promise<boolean> {
        this.currentCodec = config.codec;
        this.resizeCanvas(config.width, config.height);

        if (config.codec === VideoCodec.Jpeg) {
            // JPEG doesn't need special initialization
            this.isDecoderInitialized = true;
            return true;
        }

        if (!isWebCodecsSupported()) {
            console.warn('WebCodecs not supported, falling back to JPEG only');
            return false;
        }

        try {
            if (config.codec === VideoCodec.H264) {
                await this.initializeH264Decoder(config);
            } else if (config.codec === VideoCodec.Vp8) {
                await this.initializeVp8Decoder(config);
            }

            this.isDecoderInitialized = true;
            return true;
        } catch (error) {
            console.error('Failed to initialize video decoder:', error);
            this.events.onError?.(error as Error);
            return false;
        }
    }

    /**
     * Decode and render a frame
     */
    async decodeFrame(frame: FrameData): Promise<void> {
        const startTime = performance.now();
        this.bytesReceived += frame.data.length;

        try {
            switch (frame.codec) {
                case VideoCodec.Jpeg:
                    await this.decodeJpeg(frame);
                    break;
                case VideoCodec.H264:
                    await this.decodeH264(frame);
                    break;
                case VideoCodec.Vp8:
                    await this.decodeVp8(frame);
                    break;
                default:
                    console.warn('Unknown codec:', frame.codec);
                    // Try JPEG as fallback
                    await this.decodeJpeg(frame);
            }

            this.updateStats(startTime, frame.data.length);
        } catch (error) {
            this.droppedFrames++;
            console.error('Frame decode error:', error);
        }
    }

    /**
     * Resize the canvas
     */
    resizeCanvas(width: number, height: number): void {
        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas.width = width;
            this.canvas.height = height;

            if (this.offscreenCanvas) {
                this.offscreenCanvas.width = width;
                this.offscreenCanvas.height = height;
            }
        }
    }

    /**
     * Get current video statistics
     */
    getStats(): VideoStats {
        return {
            fps: this.currentFps,
            bitrate: this.currentBitrate,
            frameCount: this.frameCount,
            droppedFrames: this.droppedFrames,
            decodeTimeMs: this.frameCount > 0 ? this.totalDecodeTime / this.frameCount : 0,
            renderTimeMs: this.frameCount > 0 ? this.totalRenderTime / this.frameCount : 0,
            latencyMs: performance.now() - this.lastFrameTime
        };
    }

    /**
     * Reset statistics
     */
    resetStats(): void {
        this.frameCount = 0;
        this.droppedFrames = 0;
        this.totalDecodeTime = 0;
        this.totalRenderTime = 0;
        this.bytesReceived = 0;
        this.fpsCounter = 0;
        this.currentFps = 0;
        this.currentBitrate = 0;
    }

    /**
     * Clean up resources
     */
    dispose(): void {
        this.h264Decoder?.close();
        this.vp8Decoder?.close();
        this.h264Decoder = null;
        this.vp8Decoder = null;
        this.isDecoderInitialized = false;
    }

    // =========================================================================
    // Private Methods
    // =========================================================================

    private async initializeH264Decoder(config: DecoderConfig): Promise<void> {
        // Close existing decoder if any
        if (this.h264Decoder) {
            this.h264Decoder.close();
            this.h264Decoder = null;
        }

        this.h264Decoder = new VideoDecoder({
            output: (frame) => this.handleDecodedFrame(frame),
            error: (error) => {
                console.error('H264 decoder error:', error);
                this.events.onError?.(new Error(`H264 decoder error: ${error.message}`));
            }
        });

        // Try High profile first (NVENC default), then Baseline as fallback
        const codecCandidates = [
            'avc1.640028', // High Level 4.0 (NVENC default)
            'avc1.4D401F', // Main Level 3.1
            'avc1.42E01E'  // Baseline Level 3.0
        ];

        let selectedCodec: string | null = null;
        for (const codec of codecCandidates) {
            const supported = await VideoDecoder.isConfigSupported({
                codec,
                codedWidth: config.width,
                codedHeight: config.height,
                hardwareAcceleration: config.hardwareAcceleration || 'prefer-hardware'
            });
            if (supported.supported) {
                selectedCodec = codec;
                break;
            }
        }

        if (!selectedCodec) {
            throw new Error('No supported H.264 codec configuration found');
        }

        this.h264Decoder.configure({
            codec: selectedCodec,
            codedWidth: config.width,
            codedHeight: config.height,
            hardwareAcceleration: config.hardwareAcceleration || 'prefer-hardware'
        });

        console.log(`H.264 decoder initialized with codec: ${selectedCodec}`);
    }

    private async initializeVp8Decoder(config: DecoderConfig): Promise<void> {
        this.vp8Decoder = new VideoDecoder({
            output: (frame) => this.handleDecodedFrame(frame),
            error: (error) => {
                console.error('VP8 decoder error:', error);
                this.events.onError?.(new Error(`VP8 decoder error: ${error.message}`));
            }
        });

        const codecString = 'vp8';

        const supported = await VideoDecoder.isConfigSupported({
            codec: codecString,
            codedWidth: config.width,
            codedHeight: config.height,
            hardwareAcceleration: config.hardwareAcceleration || 'prefer-hardware'
        });

        if (!supported.supported) {
            throw new Error('VP8 codec configuration not supported');
        }

        this.vp8Decoder.configure({
            codec: codecString,
            codedWidth: config.width,
            codedHeight: config.height,
            hardwareAcceleration: config.hardwareAcceleration || 'prefer-hardware'
        });

        console.log('VP8 decoder initialized');
    }

    private async decodeJpeg(frame: FrameData): Promise<void> {
        const dataSlice = new Uint8Array(frame.data);
        const blob = new Blob([dataSlice], { type: 'image/jpeg' });
        const bitmap = await createImageBitmap(blob);

        this.renderBitmap(bitmap);
        bitmap.close();
    }

    private async decodeH264(frame: FrameData): Promise<void> {
        // Lazy-initialize H.264 decoder on first frame if not already set up
        if (!this.h264Decoder) {
            if (!isWebCodecsSupported()) {
                console.warn('WebCodecs not supported, cannot decode H.264');
                return;
            }
            try {
                await this.initializeH264Decoder({
                    codec: VideoCodec.H264,
                    width: frame.width,
                    height: frame.height,
                    hardwareAcceleration: 'prefer-hardware'
                });
                this.h264ReceivedKeyFrame = false;
                console.log(`H.264 decoder lazy-initialized for ${frame.width}x${frame.height}`);
            } catch (error) {
                console.error('Failed to lazy-initialize H.264 decoder:', error);
                return;
            }
        }

        // Must start decoding from a keyframe (contains SPS/PPS)
        if (!this.h264ReceivedKeyFrame) {
            if (!frame.isKeyFrame) {
                return; // Skip delta frames until first keyframe
            }
            this.h264ReceivedKeyFrame = true;
        }

        const chunk = new EncodedVideoChunk({
            type: frame.isKeyFrame ? 'key' : 'delta',
            timestamp: frame.timestamp * 1000, // Convert to microseconds
            data: frame.data
        });

        this.h264Decoder!.decode(chunk);
    }

    private async decodeVp8(frame: FrameData): Promise<void> {
        // Lazy-initialize VP8 decoder on first frame if not already set up
        if (!this.vp8Decoder) {
            if (!isWebCodecsSupported()) {
                console.warn('WebCodecs not supported, cannot decode VP8');
                return;
            }
            try {
                await this.initializeVp8Decoder({
                    codec: VideoCodec.Vp8,
                    width: frame.width,
                    height: frame.height,
                    hardwareAcceleration: 'prefer-hardware'
                });
                console.log(`VP8 decoder lazy-initialized for ${frame.width}x${frame.height}`);
            } catch (error) {
                console.error('Failed to lazy-initialize VP8 decoder:', error);
                return;
            }
        }

        const chunk = new EncodedVideoChunk({
            type: frame.isKeyFrame ? 'key' : 'delta',
            timestamp: frame.timestamp * 1000,
            data: frame.data
        });

        this.vp8Decoder!.decode(chunk);
    }

    private handleDecodedFrame(frame: VideoFrame): void {
        const renderStart = performance.now();

        // Resize canvas if needed to match decoded frame
        if (this.canvas.width !== frame.codedWidth || this.canvas.height !== frame.codedHeight) {
            this.resizeCanvas(frame.codedWidth, frame.codedHeight);
        }

        // Use OffscreenCanvas if available for better performance
        if (this.offscreenCtx && this.offscreenCanvas) {
            this.offscreenCtx.drawImage(frame, 0, 0);
            this.ctx?.drawImage(this.offscreenCanvas, 0, 0);
        } else {
            this.ctx?.drawImage(frame, 0, 0);
        }

        frame.close();

        this.totalRenderTime += performance.now() - renderStart;
    }

    private renderBitmap(bitmap: ImageBitmap): void {
        const renderStart = performance.now();

        // Resize canvas if needed
        if (this.canvas.width !== bitmap.width || this.canvas.height !== bitmap.height) {
            this.resizeCanvas(bitmap.width, bitmap.height);
        }

        this.ctx?.drawImage(bitmap, 0, 0);
        this.events.onFrameDecoded?.(bitmap);

        this.totalRenderTime += performance.now() - renderStart;
    }

    private updateStats(decodeStartTime: number, frameSize: number): void {
        const now = performance.now();

        this.frameCount++;
        this.fpsCounter++;
        this.totalDecodeTime += now - decodeStartTime;
        this.lastFrameTime = now;

        // Update FPS every second
        if (now - this.fpsLastUpdate >= 1000) {
            this.currentFps = this.fpsCounter;
            this.fpsCounter = 0;
            this.fpsLastUpdate = now;
        }

        // Update bitrate every second
        if (now - this.bitrateLastUpdate >= 1000) {
            this.currentBitrate = this.bytesReceived * 8; // bits per second
            this.bytesReceived = 0;
            this.bitrateLastUpdate = now;
        }

        // Emit stats update periodically
        if (this.frameCount % 30 === 0) {
            this.events.onStatsUpdate?.(this.getStats());
        }
    }
}

/**
 * Utility class for direct canvas rendering without decoding
 * Used for already-decoded frames or raw bitmap data
 */
export class CanvasRenderer {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d', {
            alpha: false,
            desynchronized: true
        })!;
    }

    /**
     * Render ImageBitmap to canvas
     */
    renderBitmap(bitmap: ImageBitmap): void {
        if (this.canvas.width !== bitmap.width || this.canvas.height !== bitmap.height) {
            this.canvas.width = bitmap.width;
            this.canvas.height = bitmap.height;
        }
        this.ctx.drawImage(bitmap, 0, 0);
    }

    /**
     * Render ImageData to canvas
     */
    renderImageData(imageData: ImageData): void {
        if (this.canvas.width !== imageData.width || this.canvas.height !== imageData.height) {
            this.canvas.width = imageData.width;
            this.canvas.height = imageData.height;
        }
        this.ctx.putImageData(imageData, 0, 0);
    }

    /**
     * Clear the canvas
     */
    clear(): void {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /**
     * Fill with solid color
     */
    fill(color: string): void {
        this.ctx.fillStyle = color;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /**
     * Draw text on canvas
     */
    drawText(text: string, x: number, y: number, options: {
        font?: string;
        color?: string;
        align?: CanvasTextAlign;
    } = {}): void {
        this.ctx.font = options.font || '16px Arial';
        this.ctx.fillStyle = options.color || 'white';
        this.ctx.textAlign = options.align || 'left';
        this.ctx.fillText(text, x, y);
    }
}
